import {
  DISTRIBUTION_RATIO_SCHEDULED_PER_YEAR,
  REWARD_WEIGHT_PER_COLLATERAL,
  SupportedCollaterals
} from "@astrid-dao/lib-base";
import { Provider } from "@ethersproject/abstract-provider";
import { BigNumber, Contract, Overrides, Signer } from "ethers";

import {
  ReadonlyVaultMap,
  ReadonlyUserVaultMap,
  BaiInStabilityPoolMap,
  TotalUnweightedAtidStakedValuePerCollateral
} from "../components/Dashboard/hooks/useDashboardData";
import { CollateralDecimalService } from "../components/Dashboard/models/decimals/CollateralDecimalService";
import { DashboardDecimal18 } from "../components/Dashboard/models/decimals/DashboardDecimal18";
import {
  decimalify18,
  scaleUnitOfPrecisionToWad,
  scaleWadToUnitOfPrecision
} from "../components/Dashboard/models/decimals/decimals";
import { ReadonlyVault } from "../components/Dashboard/models/vaults/ReadonlyVault";
import {
  UserReadonlyVault,
  UserReadonlyVaultStatus
} from "../components/Dashboard/models/vaults/UserReadonlyVault";
import { SELECTABLE_COLLATERAL_TYPES, BaseTokenContractNames } from "../config";
import { retriableCallWithBackoff } from "../utils/retryCall";
import {
  atidStakingMap,
  assertContractExist,
  activePoolMap,
  baseContractsMap,
  collateralTokenMap,
  currencyPriceMap,
  defaultPoolMap,
  publicSaleContractsMap,
  stabilityPoolMap,
  vaultManagerMap,
  allCollateralTokenMap
} from "./contracts";

type BaseMapTypePerCollateral = Map<SupportedCollaterals, DashboardDecimal18>;
export type Currencies = SupportedCollaterals | "ATID" | "BAI" | "xcBAI";
export type CollateralPriceMap = Map<Currencies, DashboardDecimal18>;
export type TotalDepositPerCollateral = BaseMapTypePerCollateral;
export type TotalAtidStakedValueMap = BaseMapTypePerCollateral;
export type AccountBalanceMap = Map<SupportedCollaterals, DashboardDecimal18>;

const logError = (error: unknown) => console.error("Error fetching data:", error);

// Does not throw.
async function waitForAllPromisesOrLogError<T>(promises: Promise<T>[]): Promise<T[]> {
  try {
    return await Promise.all(promises);
  } catch (e) {
    logError(e);
    return [];
  }
}

export async function getTotalUnweightedAtidStakedPerCollateral(): Promise<TotalAtidStakedValueMap> {
  const totalAtidStakedPerCollateral: TotalAtidStakedValueMap = new Map();

  const promises: Promise<any>[] = [];
  for (const [collateral] of atidStakingMap) {
    promises.push(
      (async () => {
        try {
          const atidStakingContract = assertContractExist(atidStakingMap.get(collateral));
          // The received raw totalUnweightedATIDStaked is denoted in Wei.
          // Use Decimal18.fromBigNumberString to reconstruct in Ether repr.
          const totalUnweightedATIDStaked = DashboardDecimal18.fromBigNumberString(
            (
              (await retriableCallWithBackoff(() =>
                atidStakingContract.totalUnweightedATIDStaked()
              )) as BigNumber
            ).toString()
          );

          totalAtidStakedPerCollateral.set(collateral, totalUnweightedATIDStaked);
        } catch (error) {
          logError(error);

          totalAtidStakedPerCollateral.set(collateral, DashboardDecimal18.ZERO);
        }
      })()
    );
  }

  await waitForAllPromisesOrLogError(promises);

  return totalAtidStakedPerCollateral;
}

type ContractCallResult = {
  hasFailed: boolean;
  msg?: string;
};

type CachedCollateralPriceResult = ContractCallResult & {
  price: DashboardDecimal18 | undefined;
};

type MinimumCollateralRatioResult = ContractCallResult & {
  MCR: DashboardDecimal18 | undefined;
};

type CriticalCollateralRatioResult = ContractCallResult & {
  CCR: DashboardDecimal18 | undefined;
};

type BAILiquidationReserveResult = ContractCallResult & {
  BAILiquidationReserve: DashboardDecimal18 | undefined;
};

type SignerERC20TokenBalanceResult = ContractCallResult & {
  SignerERC20TokenBalance: DashboardDecimal18 | undefined;
};

const simpleContractsResultCachePerCollateral = new Map<string, Map<string, unknown>>();
const simpleBaseContractsResultCache = new Map<string, unknown>();

SELECTABLE_COLLATERAL_TYPES.forEach(collateral => {
  simpleContractsResultCachePerCollateral.set(collateral, new Map());
});

export async function getNativeTokenBalance(signer: Signer, provider: Provider) {
  const signerAddress = await retriableCallWithBackoff(() => signer.getAddress());
  const astrBalanceResult = await retriableCallWithBackoff(() => provider.getBalance(signerAddress));

  return decimalify18(astrBalanceResult) ?? DashboardDecimal18.ZERO;
}

export async function getCachedCollateralPrice({
  collateralName,
  isTestnet
}: {
  collateralName: SupportedCollaterals;
  isTestnet: boolean;
}): Promise<CachedCollateralPriceResult> {
  const cacheKey = "price";

  try {
    const priceFeedContract = assertContractExist(currencyPriceMap.get(collateralName));
    const price: BigNumber = isTestnet
      ? await retriableCallWithBackoff(
          () => priceFeedContract?.getPrice?.() ?? Promise.resolve(BigNumber.from(0))
        )
      : await retriableCallWithBackoff(
          () => priceFeedContract?.lastFetchedPrice?.() ?? Promise.resolve(BigNumber.from(0))
        );

    const collateralPrecisionDigits =
      CollateralDecimalService.getPrecisionByCollateralType(collateralName);
    const wadPrice = decimalify18(price);
    const unitPrice = scaleWadToUnitOfPrecision(wadPrice, collateralPrecisionDigits);

    simpleContractsResultCachePerCollateral.get(collateralName)?.set(cacheKey, unitPrice);

    return { price: unitPrice, hasFailed: false, msg: "Success" };
  } catch (error: any) {
    logError(error);

    return {
      price: simpleContractsResultCachePerCollateral
        .get(collateralName)
        ?.get(cacheKey) as DashboardDecimal18,
      hasFailed: true,
      msg: error.message
    };
  }
}

export async function getMinimumCollateralRatio(
  collateral: SupportedCollaterals
): Promise<MinimumCollateralRatioResult> {
  const cacheKey = "MCR";

  try {
    const vaultManagerContract = assertContractExist(vaultManagerMap.get(collateral));
    const MCR: BigNumber = await retriableCallWithBackoff(() => vaultManagerContract.MCR());
    const mcrInWad = decimalify18(MCR);

    simpleContractsResultCachePerCollateral.get(collateral)?.set(cacheKey, mcrInWad);

    return { MCR: mcrInWad, hasFailed: false, msg: "Success" };
  } catch (error: any) {
    logError(error);

    return {
      MCR: simpleContractsResultCachePerCollateral
        .get(collateral)
        ?.get(cacheKey) as DashboardDecimal18,
      hasFailed: true,
      msg: error.message
    };
  }
}

export async function getCriticalCollateralRatio(
  collateral: SupportedCollaterals
): Promise<CriticalCollateralRatioResult> {
  const cacheKey = "CCR";

  try {
    const vaultManagerContract = assertContractExist(vaultManagerMap.get(collateral));
    const CCR: BigNumber = await retriableCallWithBackoff(() => vaultManagerContract.CCR());
    const ccrInWad = decimalify18(CCR);

    simpleContractsResultCachePerCollateral.get(collateral)?.set(cacheKey, ccrInWad);

    return { CCR: ccrInWad, hasFailed: false, msg: "Success" };
  } catch (error: any) {
    logError(error);

    return {
      CCR: simpleContractsResultCachePerCollateral
        .get(collateral)
        ?.get(cacheKey) as DashboardDecimal18,
      hasFailed: true,
      msg: error.message
    };
  }
}

export async function getBAILiquidationReserve(
  collateral: SupportedCollaterals
): Promise<BAILiquidationReserveResult> {
  const cacheKey = "BAILiquidationReserve";

  try {
    const vaultManagerContract = assertContractExist(vaultManagerMap.get(collateral));
    const BAILiquidationReserve: BigNumber = await retriableCallWithBackoff(() =>
      vaultManagerContract.BAI_GAS_COMPENSATION()
    );
    const baiLiquidationReserveInWad = decimalify18(BAILiquidationReserve);

    simpleContractsResultCachePerCollateral
      .get(collateral)
      ?.set(cacheKey, baiLiquidationReserveInWad);

    return {
      BAILiquidationReserve: baiLiquidationReserveInWad,
      hasFailed: false,
      msg: "Success"
    };
  } catch (error: any) {
    logError(error);

    return {
      BAILiquidationReserve: simpleContractsResultCachePerCollateral
        .get(collateral)
        ?.get(cacheKey) as DashboardDecimal18,
      hasFailed: true,
      msg: error.message
    };
  }
}

export async function getSignerERC20TokenBalance(
  token: BaseTokenContractNames,
  signer: Signer
): Promise<SignerERC20TokenBalanceResult> {
  try {
    const tokenContract = assertContractExist(
      baseContractsMap.get(token) ?? collateralTokenMap.get(token as SupportedCollaterals)
    );
    const balance = await retriableCallWithBackoff(async () =>
      tokenContract.balanceOf(await signer.getAddress())
    );
    const decimalifiedResult = decimalify18(balance);

    simpleContractsResultCachePerCollateral.get(token)?.set("balance", decimalifiedResult);

    return {
      SignerERC20TokenBalance: decimalifiedResult,
      hasFailed: false,
      msg: "Success"
    };
  } catch (error: any) {
    logError(error);

    return {
      SignerERC20TokenBalance: simpleContractsResultCachePerCollateral
        .get(token)
        ?.get("balance") as DashboardDecimal18,
      hasFailed: true,
      msg: error.message
    };
  }
}

export async function getAccountBalancePerCollateral(
  address: string,
  collateralTypeToSkip: string
): Promise<AccountBalanceMap> {
  const accountBalancePerCollateral: AccountBalanceMap = new Map<
    SupportedCollaterals,
    DashboardDecimal18
  >();
  const promises: Promise<any>[] = [];

  for (const [collateralName, contract] of collateralTokenMap) {
    if (collateralTypeToSkip !== collateralName) {
      promises.push(
        (async () => {
          try {
            const balance: BigNumber = await retriableCallWithBackoff(() =>
              contract.balanceOf(address)
            );
            const collateralPrecisionDigits =
              CollateralDecimalService.getPrecisionByCollateralType(collateralName);
            const decimalifiedResult = decimalify18(balance);
            const scaledDecimalResult = scaleUnitOfPrecisionToWad(
              decimalifiedResult,
              collateralPrecisionDigits
            );

            accountBalancePerCollateral.set(collateralName, scaledDecimalResult);
          } catch (error) {
            logError(error);
          }
        })()
      );
    }
  }

  await waitForAllPromisesOrLogError(promises);

  return accountBalancePerCollateral;
}

export async function getTokenBalance(address: string, token: string) {
  for (const [collateralName, contract] of allCollateralTokenMap) {
    if (token !== collateralName) continue;
    try {
      const balance: BigNumber = await contract.balanceOf(address);
      const collateralPrecisionDigits = CollateralDecimalService.getPrecisionByCollateralType(
        collateralName as SupportedCollaterals
      );
      const decimalifiedResult = decimalify18(balance);
      const scaledDecimalResult = scaleUnitOfPrecisionToWad(
        decimalifiedResult,
        collateralPrecisionDigits
      );

      return scaledDecimalResult;
    } catch (error) {
      logError(error);
    }
  }
  return DashboardDecimal18.ZERO;
}

export async function getCollateralPriceMap({
  isTestnet = false
}: {
  isTestnet?: boolean;
}): Promise<CollateralPriceMap> {
  const collateralPrices: CollateralPriceMap = new Map<SupportedCollaterals, DashboardDecimal18>();
  const promises: Promise<any>[] = [];

  for (const [collateralName] of currencyPriceMap) {
    promises.push(
      (async () => {
        const { price } = await getCachedCollateralPrice({
          collateralName,
          isTestnet
        });

        collateralPrices.set(collateralName, price ?? DashboardDecimal18.ZERO);
      })()
    );
  }

  await waitForAllPromisesOrLogError(promises);

  return collateralPrices;
}

export async function getReadonlyVaultMap(): Promise<ReadonlyVaultMap> {
  const readonlyVaultMap: ReadonlyVaultMap = new Map<SupportedCollaterals, ReadonlyVault>();
  const promises: Promise<any>[] = [];
  const cacheKey = "readOnlyVault";

  for (const [collateralName] of vaultManagerMap) {
    promises.push(
      (async () => {
        try {
          const activePoolContract = assertContractExist(activePoolMap.get(collateralName));
          const defaultPoolContract = assertContractExist(defaultPoolMap.get(collateralName));
          const [
            activePoolCollateral,
            activePoolDebt,
            defaultPoolCollateral,
            defaultPoolDebt,
            MCR,
            CCR,
            BAILiquidationReserve
          ] = await Promise.all([
            retriableCallWithBackoff((): Promise<BigNumber> => activePoolContract.getCOL()),
            retriableCallWithBackoff((): Promise<BigNumber> => activePoolContract.getBAIDebt()),
            retriableCallWithBackoff((): Promise<BigNumber> => defaultPoolContract.getCOL()),
            retriableCallWithBackoff((): Promise<BigNumber> => defaultPoolContract.getBAIDebt()),
            getMinimumCollateralRatio(collateralName),
            getCriticalCollateralRatio(collateralName),
            getBAILiquidationReserve(collateralName)
          ]);

          const collateralPrecisionDigits =
            CollateralDecimalService.getPrecisionByCollateralType(collateralName);
          const totalCollateral = activePoolCollateral.add(defaultPoolCollateral);
          const totalCollateralInCollateralUnit = decimalify18(totalCollateral);
          const totalCollateralInWad = scaleUnitOfPrecisionToWad(
            totalCollateralInCollateralUnit,
            collateralPrecisionDigits
          );
          const debt = activePoolDebt.add(defaultPoolDebt);
          const debtInWad = decimalify18(debt);

          const readOnlyVault: ReadonlyVault = new ReadonlyVault(
            collateralName,
            totalCollateralInWad,
            debtInWad,
            MCR.MCR,
            CCR.CCR,
            BAILiquidationReserve.BAILiquidationReserve
          );

          readonlyVaultMap.set(collateralName, readOnlyVault);
          simpleContractsResultCachePerCollateral.get(collateralName)?.set(cacheKey, readOnlyVault);
        } catch (error) {
          logError(error);

          const cachedResult = simpleContractsResultCachePerCollateral
            .get(collateralName)
            ?.get(cacheKey) as ReadonlyVault;
          const emptyReadOnlyVault: ReadonlyVault = new ReadonlyVault(collateralName);

          readonlyVaultMap.set(collateralName, cachedResult ?? emptyReadOnlyVault);
        }
      })()
    );
  }

  await waitForAllPromisesOrLogError(promises);

  return readonlyVaultMap;
}

export async function getUserReadonlyVaultMap(signerAddress: string): Promise<ReadonlyUserVaultMap> {
  const readonlyUserVaultMap: ReadonlyUserVaultMap = new Map<
    SupportedCollaterals,
    UserReadonlyVault
  >();
  const promises: Promise<any>[] = [];
  const cacheKey = "readonlyUserVault";

  for (const [collateralName, vaultManagerContract] of vaultManagerMap) {
    promises.push(
      (async () => {
        try {
          const [status, collateral, debt] = await Promise.all([
            retriableCallWithBackoff(
              (): Promise<UserReadonlyVaultStatus> =>
                vaultManagerContract.getVaultStatus(signerAddress)
            ),
            retriableCallWithBackoff(
              (): Promise<BigNumber> => vaultManagerContract.getVaultColl(signerAddress)
            ),
            retriableCallWithBackoff(
              (): Promise<BigNumber> => vaultManagerContract.getVaultDebt(signerAddress)
            )
          ]);

          const collateralPrecisionDigits =
            CollateralDecimalService.getPrecisionByCollateralType(collateralName);
          const collateralInCollateralUnit = decimalify18(collateral);
          const collateralInWad = scaleUnitOfPrecisionToWad(
            collateralInCollateralUnit,
            collateralPrecisionDigits
          );
          const debtInWad = decimalify18(debt);

          const readonlyUserVault: UserReadonlyVault = new UserReadonlyVault(
            collateralName,
            signerAddress,
            status,
            collateralInWad,
            debtInWad
          );

          readonlyUserVaultMap.set(collateralName, readonlyUserVault);
          simpleContractsResultCachePerCollateral
            .get(collateralName)
            ?.set(cacheKey, readonlyUserVault);
        } catch (error) {
          logError(error);

          const cachedResult = simpleContractsResultCachePerCollateral
            .get(collateralName)
            ?.get(cacheKey) as UserReadonlyVault;
          const readonlyUserVault: UserReadonlyVault = new UserReadonlyVault(
            collateralName,
            signerAddress,
            "nonExistent"
          );
          readonlyUserVaultMap.set(collateralName, cachedResult ?? readonlyUserVault);
        }
      })()
    );
  }

  await waitForAllPromisesOrLogError(promises);

  return readonlyUserVaultMap;
}

export async function getTotalBAIIssuanceByBAIToken(): Promise<DashboardDecimal18> {
  const BAITokenContract = assertContractExist(baseContractsMap.get("baiToken"));
  const cacheKey = "totalSupply";

  try {
    const totalSupply: BigNumber = await retriableCallWithBackoff(() =>
      BAITokenContract.totalSupply()
    );
    const totalSupplyInWad = decimalify18(totalSupply);

    simpleBaseContractsResultCache.set(cacheKey, totalSupplyInWad);

    return totalSupplyInWad;
  } catch (error) {
    logError(error);

    return (
      (simpleBaseContractsResultCache.get(cacheKey) as DashboardDecimal18) ?? DashboardDecimal18.ZERO
    );
  }
}

export async function getBAIInStabilityPoolForEachCollateral(): Promise<BaiInStabilityPoolMap> {
  const promises: Promise<any>[] = [];
  const baiInStabilityPoolPerCollateral: BaiInStabilityPoolMap = new Map();
  const cacheKey = "baiAmountInPool";

  for (const [collateralName] of vaultManagerMap) {
    promises.push(
      (async () => {
        try {
          const stabilityPoolContract = assertContractExist(stabilityPoolMap.get(collateralName));
          const baiAmountInPool: BigNumber = await retriableCallWithBackoff(() =>
            stabilityPoolContract.getTotalBAIDeposits()
          );
          const poolBaiAmountInWad = decimalify18(baiAmountInPool);

          baiInStabilityPoolPerCollateral.set(collateralName, poolBaiAmountInWad);

          simpleContractsResultCachePerCollateral
            .get(collateralName)
            ?.set(cacheKey, poolBaiAmountInWad);
        } catch (error) {
          logError(error);

          const cachedResult = simpleContractsResultCachePerCollateral
            .get(collateralName)
            ?.get(cacheKey) as DashboardDecimal18;
          baiInStabilityPoolPerCollateral.set(
            collateralName,
            cachedResult ?? DashboardDecimal18.ZERO
          );
        }
      })()
    );
  }

  await waitForAllPromisesOrLogError(promises);

  return baiInStabilityPoolPerCollateral;
}

export async function getTotalBAIInAllStabilityPools(): Promise<DashboardDecimal18> {
  let baiAmount: DashboardDecimal18 = DashboardDecimal18.ZERO;
  const baiInStabilityPoolPerCollateral = await getBAIInStabilityPoolForEachCollateral();

  for (const [, amount] of baiInStabilityPoolPerCollateral) {
    baiAmount = baiAmount.add(amount);
  }

  return baiAmount;
}

export async function getTotalUnweightedAtidStakedValueForEachCollateral(
  atidPrice: DashboardDecimal18
): Promise<TotalUnweightedAtidStakedValuePerCollateral> {
  const totalAtidStakedValuePerCollateral: TotalUnweightedAtidStakedValuePerCollateral = new Map();
  const promises: Promise<any>[] = [];
  const cacheKey = "totalAtidStakedValue";

  for (const [collateralName] of vaultManagerMap) {
    promises.push(
      (async () => {
        try {
          const atidStakingContract = assertContractExist(atidStakingMap.get(collateralName));
          // The received raw totalUnweightedATIDStaked is denoted in Wei.
          // Use Decimal18.fromBigNumberString to reconstruct in Ether repr.
          const totalUnweightedATIDStaked = DashboardDecimal18.fromBigNumberString(
            (
              (await retriableCallWithBackoff(() =>
                atidStakingContract.totalUnweightedATIDStaked()
              )) as BigNumber
            ).toString()
          );
          const totalAtidStakedValue = totalUnweightedATIDStaked.mul(atidPrice);

          totalAtidStakedValuePerCollateral.set(collateralName, totalAtidStakedValue);
          simpleContractsResultCachePerCollateral
            .get(collateralName)
            ?.set(cacheKey, totalAtidStakedValue);
        } catch (error) {
          logError(error);

          const cachedResult = simpleContractsResultCachePerCollateral
            .get(collateralName)
            ?.get(cacheKey) as DashboardDecimal18;
          totalAtidStakedValuePerCollateral.set(
            collateralName,
            cachedResult ?? DashboardDecimal18.ZERO
          );
        }
      })()
    );
  }

  await waitForAllPromisesOrLogError(promises);

  return totalAtidStakedValuePerCollateral;
}

export async function getTotalAtidStakedValue(
  atidPrice: DashboardDecimal18
): Promise<DashboardDecimal18> {
  const totalAtidStakedValuePerCollateral = await getTotalUnweightedAtidStakedValueForEachCollateral(
    atidPrice
  );

  let totalUnweightedATIDStakedTotal: DashboardDecimal18 = DashboardDecimal18.ZERO;

  for (const [, value] of totalAtidStakedValuePerCollateral) {
    totalUnweightedATIDStakedTotal = totalUnweightedATIDStakedTotal.add(value);
  }

  return totalUnweightedATIDStakedTotal;
}

export function getStabilityPoolApr({
  baiInStabilityPool,
  remainingStabilityPoolAtidReward,
  scaledAtidPriceInEthers,
  rewardWeight
}: {
  baiInStabilityPool: DashboardDecimal18;
  remainingStabilityPoolAtidReward: DashboardDecimal18;
  scaledAtidPriceInEthers: DashboardDecimal18;
  rewardWeight: number;
}): DashboardDecimal18 {
  const hasZeroValue = remainingStabilityPoolAtidReward.isZero || baiInStabilityPool.isZero;
  let stabilityPoolApr = DashboardDecimal18.ZERO;

  if (!hasZeroValue && !scaledAtidPriceInEthers.isZero) {
    const distributedAtidInOneYear = remainingStabilityPoolAtidReward.mul(
      DISTRIBUTION_RATIO_SCHEDULED_PER_YEAR
    );
    const distributedAtidOneYearInUSD = distributedAtidInOneYear.mul(scaledAtidPriceInEthers);
    stabilityPoolApr = distributedAtidOneYearInUSD.mulDiv(rewardWeight, baiInStabilityPool);
  }

  return stabilityPoolApr;
}

export async function getRemainingStabilityPoolAtidReward(
  communityIssuanceContract: Contract
): Promise<DashboardDecimal18> {
  let issuanceCapInWad: DashboardDecimal18;
  let totalAtidIssuedInWad: DashboardDecimal18;
  const issuanceCapCacheKey = "issuanceCap";
  const totalAtidIssuedCacheKey = "totalAtidIssued";

  try {
    const atidSupplyCapResult: BigNumber = await retriableCallWithBackoff(() =>
      communityIssuanceContract.ATIDSupplyCap()
    );
    issuanceCapInWad = decimalify18(atidSupplyCapResult);

    simpleBaseContractsResultCache.set(issuanceCapCacheKey, issuanceCapInWad);
  } catch (error) {
    logError(error);

    issuanceCapInWad = simpleBaseContractsResultCache.get(issuanceCapCacheKey) as DashboardDecimal18;
  }

  try {
    const totalAtidIssuedResult: BigNumber = await retriableCallWithBackoff(() =>
      communityIssuanceContract.totalATIDIssued()
    );
    totalAtidIssuedInWad = decimalify18(totalAtidIssuedResult);

    simpleBaseContractsResultCache.set(totalAtidIssuedCacheKey, totalAtidIssuedInWad);
  } catch (error) {
    logError(error);

    totalAtidIssuedInWad = simpleBaseContractsResultCache.get(
      totalAtidIssuedCacheKey
    ) as DashboardDecimal18;
  }

  return issuanceCapInWad.sub(totalAtidIssuedInWad);
}

export async function getStabilityPoolPerCollateral(
  atidPrice: DashboardDecimal18
): Promise<Map<SupportedCollaterals, DashboardDecimal18>> {
  const stabilityPoolAprMap = new Map<SupportedCollaterals, DashboardDecimal18>();
  const promises: Promise<any>[] = [];
  const cacheKey = "stabilityPoolApr";

  for (const [collateralName] of vaultManagerMap) {
    promises.push(
      (async () => {
        try {
          const stabilityPoolContract = assertContractExist(stabilityPoolMap.get(collateralName));
          const communityIssuanceContract = assertContractExist(
            baseContractsMap.get("communityIssuance")
          );
          const baiInStabilityPool: BigNumber = await retriableCallWithBackoff(() =>
            stabilityPoolContract.getTotalBAIDeposits()
          );
          const remainingStabilityPoolAtidReward = await getRemainingStabilityPoolAtidReward(
            communityIssuanceContract
          );

          const stabilityPoolApr = getStabilityPoolApr({
            baiInStabilityPool: decimalify18(baiInStabilityPool),
            remainingStabilityPoolAtidReward,
            scaledAtidPriceInEthers: atidPrice,
            rewardWeight: REWARD_WEIGHT_PER_COLLATERAL.get(collateralName) ?? 0
          });

          stabilityPoolAprMap.set(collateralName, stabilityPoolApr);
          simpleContractsResultCachePerCollateral
            .get(collateralName)
            ?.set(cacheKey, stabilityPoolApr);
        } catch (error) {
          logError(error);

          const cachedResult = simpleContractsResultCachePerCollateral
            .get(collateralName)
            ?.get(cacheKey) as DashboardDecimal18;

          stabilityPoolAprMap.set(collateralName, cachedResult ?? DashboardDecimal18.ZERO);
        }
      })()
    );
  }

  await waitForAllPromisesOrLogError(promises);

  return stabilityPoolAprMap;
}

export function approveCeUSDC({
  amount,
  signer,
  overrides
}: {
  amount: DashboardDecimal18;
  signer: Signer;
  overrides: Overrides;
}) {
  const ceUsdcContract = assertContractExist(collateralTokenMap.get("ceUSDC")).connect(signer);
  const crowdsale = assertContractExist(publicSaleContractsMap.get("crowdsale"));

  return ceUsdcContract.approve(crowdsale.address, amount.hex, overrides);
}

export function buyAtidTokens({
  account,
  amount,
  signer,
  overrides
}: {
  account: string;
  amount: DashboardDecimal18;
  signer: Signer;
  overrides: Overrides;
}) {
  const crowdsale = assertContractExist(publicSaleContractsMap.get("crowdsale")).connect(signer);

  return crowdsale.buyTokens(account, amount.hex, overrides);
}
